// API Configuration
export const API_CONFIG = {
  TIMEOUT: 15000, // 15 seconds
  BASE_URL: process.env.REACT_APP_API_URL || 'https://backend.newpays.co/api',
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://backend.newpays.co' 
      : 'http://localhost:5000')
};

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    SIGNIN: '/auth/signin',
    SIGNUP: '/auth/signup',
    ADMIN_SIGNIN: '/auth/admin/signin',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    OAUTH: (provider) => `/api/oauth/${provider.toLowerCase()}`,
    TWO_FACTOR: {
      STATUS: '/auth/2fa/status',
      SETUP: '/auth/2fa/setup',
      VERIFY: '/auth/2fa/verify',
      VERIFY_SETUP: '/auth/2fa/verify-setup',
      DISABLE: '/auth/2fa/disable',
      DISABLE_REQUEST: '/auth/2fa/disable/request',
      DISABLE_CONFIRM: '/auth/2fa/disable/confirm'
    }
  },
  // User
  USER: {
    PROFILE: '/user/profile',
    UPDATE_PROFILE: '/user/profile',
    UPDATE_EMAIL: '/user/email',
    UPDATE_WALLET: '/user/wallet',
    INITIATE_WALLET_CHANGE: '/user/wallet/initiate',
    UPDATE_PASSWORD: '/user/password',
    DEVICES: '/user/devices'
  },
  // Bank
  BANK: {
    CREATE: '/bank/create',
    SAVE_PERSONAL_INFO: '/bank/save-personal-info',
    REGISTER_CUSTOMER: '/bank/register-customer',
    REGISTRATION_STATUS: '/bank/registration-status',
    KYC_EMBED: '/bank/kyc-embed',
    KYC_STATUS: '/bank/kyc-status',
    SYNC_EVENTS: '/bank/sync-events',
    REQUEST_ACCOUNT: '/bank/request-account',
    ACCOUNT_STATUSES: '/bank/account-statuses',
    CREATE_ACCOUNT: '/bank/create-account',
    INITIATE_ACCOUNT_WALLET_CHANGE: '/bank/initiate-account-wallet-change',
    UPDATE_ACCOUNT_WALLET: '/bank/update-account-wallet'
  },
  // Team
  TEAM: {
    DASHBOARD: '/team/dashboard',
    MEMBERS: '/team/members',
    CREATE_MEMBER: '/team/members',
    SYNC_EVENTS: '/team/sync-events'
  },
  // Admin
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    ACCOUNT_REQUESTS: '/admin/account-requests',
    USER_DEVICES: (userId) => `/admin/users/${userId}/devices`,
    APPROVE_ACCOUNT_REQUEST: (id) => `/admin/users/${id}/approve-account-request`,
    REJECT_ACCOUNT_REQUEST: (id) => `/admin/users/${id}/reject-account-request`,
    WEBHOOKS: '/admin/webhooks',
    WEBHOOK_LOGS: (id) => `/admin/webhooks/${id}/logs`,
    WEBHOOK_SETUP: '/admin/webhooks/setup',
    WEBHOOK_EVENTS: '/admin/webhook-events',
    WEBHOOK_EVENTS_STATS: '/admin/webhook-events/stats',
    WEBHOOK_EVENT_DETAIL: (id) => `/admin/webhook-events/${id}`,
    TRANSACTIONS: '/admin/transactions',
    PAYMENT_VOLUME: '/admin/payment-volume',
    SYNC_ALL_EVENTS: '/admin/sync-all-events',
    SYNC_USER_EVENTS: (id) => `/admin/users/${id}/sync-events`,
    EMAIL_SEND: '/admin/email/send',
    EMAIL_USERS: '/admin/email/users',
    NOTIFICATION_COUNTS: '/admin/notifications/counts'
  },
  // Team Leader Request
  TEAM_LEADER_REQUEST: {
    REQUEST: '/team-leader-request/request',
    MY_REQUEST: '/team-leader-request/my-request',
    ALL: '/team-leader-request/all',
    APPROVE: (id) => `/team-leader-request/${id}/approve`,
    REJECT: (id) => `/team-leader-request/${id}/reject`
  },
  // Card
  CARD: {
    DETAILS: '/card/details',
    DESIGNS: '/card/designs',
    TRANSACTIONS: '/card/transactions',
    AUTHORIZATIONS: '/card/authorizations',
    PROVISION: '/card/provision',
    FREEZE: '/card/freeze',
    UNFREEZE: '/card/unfreeze',
    PIN_UPDATE_URL: '/card/pin-update-url',
    EPHEMERAL_KEY: '/card/ephemeral-key'
  },
  // Live Chat
  LIVE_CHAT: {
    CONVERSATION: '/live-chat/conversation',
    CONVERSATIONS: '/live-chat/conversations',
    CONVERSATION_DETAIL: (id) => `/live-chat/conversations/${id}`,
    CONVERSATION_MESSAGE: (id) => `/live-chat/conversations/${id}/message`,
    CONVERSATION_CLOSE: (id) => `/live-chat/conversations/${id}/close`,
    CONVERSATION_APPROVE: (id) => `/live-chat/conversations/${id}/approve`,
    CONVERSATION_USER_CLOSE: (id) => `/live-chat/conversations/${id}/user-close`,
    REQUEST: '/live-chat/request',
    MESSAGE: '/live-chat/message'
  },
  // Chat
  CHAT: {
    MESSAGE: '/chat/message'
  }
};

// Routes
export const ROUTES = {
  HOME: '/signin',
  SIGNIN: '/signin',
  SIGNUP: '/signup',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  EDIT_PROFILE: '/edit-profile',
  DEVICE_HISTORY: '/device-history',
  CHANGE_PASSWORD: '/change-password',
  BANK: '/bank',
  BANK_SETUP: '/bank-setup',
  TRANSACTION_HISTORY: '/transaction-history',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_EMAIL: '/verify-email',
  OAUTH_CALLBACK: '/oauth/callback',
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
  // Team routes
  TEAM: {
    DASHBOARD: '/team/dashboard',
    TRANSACTIONS: '/team/transactions',
    MEMBER_DETAIL: (id) => `/team/members/${id}`
  },
  // Admin routes
  ADMIN: {
    LOGIN: '/admin/login',
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAIL: (id) => `/admin/users/${id}`,
    ACCOUNT_REQUESTS: '/admin/account-requests',
    TRANSACTIONS: '/admin/transactions',
    PAYMENT_VOLUME: '/admin/payment-volume',
    TEAM_LEADER_REQUESTS: '/admin/team-leader-requests',
    LIVE_CHAT: '/admin/live-chat',
    WEBHOOKS: '/admin/webhooks',
    EMAIL: '/admin/email'
  }
};

// Blockchain Explorer URLs
export const BLOCKCHAIN_EXPLORERS = {
  polygon: 'https://polygonscan.com/tx/',
  ethereum: 'https://etherscan.com/tx/',
  base: 'https://basescan.org/tx/',
  arbitrum: 'https://arbiscan.io/tx/',
  optimism: 'https://optimistic.etherscan.io/tx/',
  avalanche_c_chain: 'https://snowtrace.io/tx/',
  tron: 'https://tronscan.org/#/transaction/',
  solana: 'https://solscan.io/tx/'
};

// Validation Rules
export const VALIDATION = {
  PASSWORD: {
    MIN_LENGTH: 6
  },
  TWO_FACTOR: {
    CODE_LENGTH: 6
  },
  TEAM_LEADER_REASON: {
    MAX_LENGTH: 500
  }
};

// UI Constants
export const UI = {
  CAROUSEL: {
    AUTO_ROTATE_INTERVAL: 5000, // 5 seconds
    SLIDE_COUNT: 4
  },
  COPY_FEEDBACK_DURATION: 2000, // 2 seconds
  SUCCESS_MESSAGE_DURATION: 1500, // 1.5 seconds
  REDIRECT_DELAY: 100, // milliseconds
  CONTACT_FORM_RESET_DELAY: 3000, // 3 seconds
  CHAT_POLL_INTERVAL: 2000, // 2 seconds
  CHAT_ESCALATION_DELAY: 500, // 0.5 seconds
  ADDRESS_INIT_DELAY: 100, // 0.1 seconds
  ADDRESS_DROPDOWN_DELAY: 500, // 0.5 seconds
  MAX_MESSAGE_LENGTH_LIVE_CHAT: 2000,
  MAX_MESSAGE_LENGTH_CHAT: 1000,
  MOBILE_BREAKPOINT: 768 // pixels - mobile breakpoint for responsive design
};

// Webhook Event Categories
export const WEBHOOK_EVENT_CATEGORIES = [
  'customer',
  'kyc_link',
  'liquidation_address.drain',
  'static_memo.activity',
  'transfer',
  'virtual_account.activity',
  'card_account',
  'card_transaction',
  'posted_card_account_transaction',
  'card_withdrawal'
];

// User Roles
export const USER_ROLES = {
  USER: 'user',
  TEAM_LEADER: 'team_leader',
  TEAM_MEMBER: 'team_member',
  ADMIN: 'admin'
};

// Local Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  ADMIN_TOKEN: 'adminToken',
  TEAM_MEMBER_WORKFLOW: 'teamMemberWorkflow',
  ACCOUNT_VIEW: 'accountView',
  TWO_FACTOR_BACKUP_CODES_DISMISSED: 'twoFactorBackupCodesDismissed'
};
